import React, { type SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Section } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import {
	QueueListSection,
	useSelectedQueueSectionByCategory,
} from '@atlassian/jira-servicedesk-queues-categorized-store/src/controllers/navigation-category/index.tsx';
import { useCategorizedGroupsNavItems } from '@atlassian/jira-servicedesk-queues-categorized-store/src/controllers/priority-group/index.tsx';
import { useCategorizedNavItems } from '@atlassian/jira-servicedesk-queues-categorized-store/src/main.tsx';
import type { ItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { QueueList } from '../queue-list/main.tsx';
import {
	getAllQueuesStateFromLocalStorage,
	setAllQueuesStateInLocalStorage,
} from './local-storage/index.tsx';
import { messages } from './messages.tsx';

type AllQueuesSectionProps = {
	practice: ItsmPractice;
};

const AllQueueButton = ({
	expanded,
	onClick,
}: {
	expanded: boolean;
	onClick: (e: SyntheticEvent<HTMLElement>) => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<CollapsibleSectionContainer aria-expanded={expanded} onClick={onClick} type="button">
			{expanded ? (
				<ChevronDownIcon spacing="spacious" label="" color={token('color.text')} />
			) : (
				<ChevronRightIcon spacing="spacious" label="" color={token('color.text')} />
			)}
			{fg('fix_jsm_queue_insertbefore_error') ? (
				<Box as="span">{formatMessage(messages.allQueues)}</Box>
			) : (
				formatMessage(messages.allQueues)
			)}
		</CollapsibleSectionContainer>
	);
};

const AllQueuesSection = ({ practice }: AllQueuesSectionProps): JSX.Element | null => {
	const { data: project } = useProject();
	const projectKey = project?.key || '';
	const projectId = project?.id.toString() || '';
	const [allQueues] = useCategorizedNavItems('', projectKey, practice);
	const [groups] = useCategorizedGroupsNavItems(projectKey, practice);

	const [pathCategorySelectedSection] = useSelectedQueueSectionByCategory(practice);
	const isAllQueuesSelected = pathCategorySelectedSection === QueueListSection.ALL_QUEUES;

	const [expandHiddenSection, setExpandHiddenSection] = useState(false);

	useEffect(() => {
		const stateFromLocalStorage = getAllQueuesStateFromLocalStorage(projectId);
		setExpandHiddenSection(
			stateFromLocalStorage === undefined ? isAllQueuesSelected : stateFromLocalStorage,
		);
	}, [groups.length, isAllQueuesSelected, projectId]);

	const handleClick = useCallback(
		(e: SyntheticEvent<HTMLElement>) => {
			// Keep the focus when using space or enter key.
			e.stopPropagation();
			setExpandHiddenSection(!expandHiddenSection);
			setAllQueuesStateInLocalStorage(projectId, !expandHiddenSection);
		},
		[expandHiddenSection, projectId],
	);

	if (!allQueues || allQueues.length === 0) {
		return null;
	}
	return (
		<Section>
			<AllQueueButton expanded={expandHiddenSection} onClick={handleClick} />
			{expandHiddenSection && (
				<QueueList queues={allQueues} section={QueueListSection.ALL_QUEUES} />
			)}
		</Section>
	);
};

export default AllQueuesSection;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleSectionContainerLegacy = styled.button({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	marginTop: token('space.200'),
	marginBottom: token('space.100'),
	border: 'none',
	backgroundColor: 'transparent',
	width: '100%',
	padding: 0,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	textTransform: 'uppercase',
	font: token('font.body.small'),
	fontWeight: token('font.weight.bold'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleSectionContainerNew = styled.button({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	marginTop: token('space.200'),
	marginBottom: token('space.100'),
	border: 'none',
	backgroundColor: 'transparent',
	width: '100%',
	padding: 0,
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.bold'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
});

const CollapsibleSectionContainer = componentWithFG(
	'queue-groups-nav3-minor-ui-updates',
	CollapsibleSectionContainerNew,
	CollapsibleSectionContainerLegacy,
);
